import {
  AmountInputField,
  CheckboxField,
  RadioGroupField,
  SegmentedRadioGroupField,
  SelectField,
} from '@hex/calculators-shared/components';
import { INTEREST_RATE_CALCULATION_DATE } from '@hex/calculators-shared/constants';
import { formatCalculationDate } from '@hex/calculators-shared/utils';
import {
  Icon,
  Flex,
  tokens,
  Label,
  Button,
  Radio,
  SegmentedRadio,
  HelpButton,
  PopoverTrigger,
  IconButton,
  SelectItem,
} from '@sapphire-react/core';
import { Help, Sack } from '@sapphire/icons/react';
import { RefObject, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { CURRENT_INTEREST } from '../../constants';
import { LoanCalculatorForm } from '../../types';
import { useCostForm } from '../../useCostForm';
import { formatNumber } from '../../utils';
import { RefundMethodTooltip } from './components/RefundMethodTooltip';

type Props = {
  buttonRef: RefObject<any>;
  loading?: boolean;
  onSubmit: (data: LoanCalculatorForm) => void;
  resetForm: () => void;
  data?: LoanCalculatorForm;
};

export const Form = ({ onSubmit, buttonRef, loading, resetForm, data }: Props) => {
  const { t } = useTranslation();

  const { control, handleSubmit, trackCalculatorStarted, allFields, maturityFieldRules, interestFieldRules } =
    useCostForm();

  useEffect(() => {
    if (JSON.stringify(allFields) !== JSON.stringify(data)) {
      resetForm();
    }
  }, [allFields, data, resetForm]);

  const getYearSelectOptions = () => {
    const yearsSelectItems = [];

    for (let i = 18; i < 65; i++) {
      yearsSelectItems.push(<SelectItem key={i.toString()}>{i.toString()}</SelectItem>);
    }

    return yearsSelectItems;
  };

  return (
    <S.Form
      onSubmit={handleSubmit((formData) => onSubmit(formData))}
      autoComplete={'off'}
      onClick={trackCalculatorStarted}
    >
      <Flex flexDirection={'column'} gap={tokens.size.spacingMd}>
        <AmountInputField
          control={control}
          name={'loanAmount'}
          label={t('form.loanAmount.label')}
          width={'100%'}
          data-cblabel={t('form.loanAmount.label')}
          marginTop={tokens.size.spacingMd.value}
          postfix={'€'}
          min={0}
          max={10_000_000}
          placeholder={t('form.loanAmount.placeholder')}
          prefix={
            <Icon size={'md'}>
              <Sack />
            </Icon>
          }
          rules={{
            required: t('form.loanAmount.error'),
          }}
        />

        <S.Grid>
          <AmountInputField
            control={control}
            name={'maturity'}
            label={t('form.maturity.label')}
            width={'100%'}
            type={'number'}
            postfix={'v'}
            defaultValue={control?._defaultValues?.maturity?.toString()}
            data-cblabel={t('form.maturity.label')}
            rules={maturityFieldRules}
            min={1}
            max={40}
            contextualHelp={
              <HelpButton variant={'help'} aria-label={t('help')} contentBody={t('form.maturity.tooltip')} />
            }
          />
          <AmountInputField
            control={control}
            name={'interest'}
            label={t('form.interestRate.label')}
            width={'100%'}
            postfix={'%'}
            data-cblabel={t('form.interestRate.label')}
            inputMode={'decimal'}
            rules={interestFieldRules}
            contextualHelp={
              <HelpButton
                variant={'help'}
                aria-label={t('help')}
                contentBody={t('form.interestRate.tooltip', {
                  currentInterest: formatNumber(CURRENT_INTEREST, true),
                  date: formatCalculationDate(INTEREST_RATE_CALCULATION_DATE),
                })}
              />
            }
          />
        </S.Grid>

        <Label
          aria-label={t('form.repaymentMethod.label')}
          htmlFor={'repaymentMethod'}
          style={{ display: 'inline' }}
          contextualHelp={
            <PopoverTrigger popoverContent={<RefundMethodTooltip />} noMaxWidth>
              <IconButton variant={'tertiary'} aria-label={'Show notifications'} size={'sm'}>
                <Help />
              </IconButton>
            </PopoverTrigger>
          }
        >
          {t('form.repaymentMethod.label')}
        </Label>
        <S.RadioWrapper>
          <SegmentedRadioGroupField
            control={control}
            aria-label={t('form.repaymentMethod.label')}
            name={'repaymentMethod'}
            marginTop={-10}
            UNSAFE_style={{
              border: `1px solid ${tokens.color.borderSecondary.value}`,
              borderRadius: 100,
              boxSizing: 'border-box',
              backgroundColor: tokens.color.backgroundSurface.value,
            }}
          >
            <SegmentedRadio
              value={'Annuity'}
              data-cblabel={t('form.repaymentMethod.options.annuity')}
              aria-label={t('form.repaymentMethod.options.annuity')}
              UNSAFE_style={{ textAlign: 'center', width: '100%', display: 'flex' }}
            >
              {t('form.repaymentMethod.options.annuity')}
            </SegmentedRadio>
            <SegmentedRadio
              value={'FixedAnnuity'}
              data-cblabel={t('form.repaymentMethod.options.fixedAnnuity')}
              aria-label={t('form.repaymentMethod.options.fixedAnnuity')}
              UNSAFE_style={{ textAlign: 'center', width: '100%', display: 'flex' }}
            >
              {t('form.repaymentMethod.options.fixedAnnuity')}
            </SegmentedRadio>
            <SegmentedRadio
              value={'Serial'}
              data-cblabel={t('form.repaymentMethod.options.serial')}
              aria-label={t('form.repaymentMethod.options.serial')}
              UNSAFE_style={{ textAlign: 'center', width: '100%', display: 'flex' }}
            >
              {t('form.repaymentMethod.options.serial')}
            </SegmentedRadio>
          </SegmentedRadioGroupField>
        </S.RadioWrapper>

        <S.InputWrapper>
          <Label aria-label={t('form.LoanSecurity')} htmlFor={'loanRepaymentSecurity'}>
            {t('form.LoanSecurity')}
          </Label>
          <CheckboxField name={'loanRepaymentSecurity'} control={control} marginTop={6}>
            {t('form.calculateRepaymentSecurity')}
          </CheckboxField>
        </S.InputWrapper>

        {allFields.loanRepaymentSecurity && (
          <>
            <S.RadioWrapper>
              <RadioGroupField
                control={control}
                orientation={'horizontal'}
                name={'lsiLevel'}
                label={t('form.securityLevel.label')}
                UNSAFE_style={{ width: '100%', flexWrap: 'nowrap' }}
                contextualHelp={
                  <HelpButton variant={'help'} aria-label={t('help')} contentBody={t('form.securityLevel.tooltip')} />
                }
              >
                <Radio
                  value={t('form.securityLevel.options.high')}
                  data-cblabel={t('form.securityLevel.options.high')}
                  aria-label={t('form.securityLevel.options.high')}
                  UNSAFE_style={{ display: 'flex' }}
                >
                  {t('form.securityLevel.options.high')}
                </Radio>
                <Radio
                  value={t('form.securityLevel.options.medium')}
                  data-cblabel={t('form.securityLevel.options.medium')}
                  aria-label={t('form.securityLevel.options.medium')}
                  UNSAFE_style={{ display: 'flex' }}
                >
                  {t('form.securityLevel.options.medium')}
                </Radio>
                <Radio
                  value={t('form.securityLevel.options.low')}
                  data-cblabel={t('form.securityLevel.options.low')}
                  aria-label={t('form.securityLevel.options.low')}
                  width={'100%'}
                  UNSAFE_style={{ display: 'flex' }}
                >
                  {t('form.securityLevel.options.low')}
                </Radio>
              </RadioGroupField>
            </S.RadioWrapper>

            <RadioGroupField
              control={control}
              orientation={'horizontal'}
              name={'insuredPeople'}
              label={t('form.numberOfApplicants.label')}
              marginTop={tokens.size.spacing2xs.value}
            >
              <Radio
                value={'1'}
                id={'single_person'}
                data-test-id={'single_person'}
                data-cblabel={'Vakuutettavien henkilöiden määrä'}
              >
                {t('form.numberOfApplicants.options.single')}
              </Radio>
              <Radio
                value={'2'}
                id={'married'}
                data-test-id={'married'}
                data-cblabel={'Vakuutettavien henkilöiden määrä'}
              >
                {t('form.numberOfApplicants.options.joint')}
              </Radio>
            </RadioGroupField>

            <SelectField
              control={control}
              name={'firstAge'}
              data-test-id={'firstAge'}
              label={t('form.firstBirth.label')}
              width={'100%'}
              rules={{
                required: t('form.firstBirth.error'),
              }}
            >
              {getYearSelectOptions()}
            </SelectField>
            {allFields.insuredPeople === '2' && (
              <SelectField
                control={control}
                name={'secondAge'}
                data-test-id={'secondAge'}
                label={t('form.secondBirth.label')}
                width={'100%'}
                rules={{
                  required: t('form.secondBirth.error'),
                }}
              >
                {getYearSelectOptions()}
              </SelectField>
            )}
          </>
        )}

        <Flex justifyContent={'center'} marginTop={tokens.size.spacingSm}>
          <Button ref={buttonRef} type={'submit'} isDisabled={loading} name={'submit'} data-test-id={'form-submit'}>
            {loading ? t('loading') : t('form.submit')}
          </Button>
        </Flex>
      </Flex>
    </S.Form>
  );
};

const S = {
  Form: styled.form`
    display: flex;
    flex-direction: column;
  `,
  Grid: styled.div`
    grid-template-columns: 1fr 1fr;
    column-gap: 16px;
    width: 100%;
    display: grid;
  `,
  InputWrapper: styled.div`
    margin-top: ${tokens.size.spacingSm.value};
  `,
  RadioWrapper: styled.div`
    div[role='radiogroup'] {
      flex-wrap: nowrap;
      width: 100%;
      box-sizing: border-box;

      @media (min-width: 768px) and (max-width: 868px) {
        max-width: 325px;
      }
    }
    label {
      display: inline;
    }
  `,
};
